import React, { useState, useEffect, useRef } from "react";
import {
  Container, Tabs, Tab, Box, Button, TextField, Table, TableHead, TableRow,
  TableCell, TableBody, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Select, IconButton, TablePagination, Snackbar, Alert, Typography
} from "@mui/material";
import { Add, Edit, Delete, Undo } from "@mui/icons-material";
import { axiosInstances } from "./apiConfig";

export default function ProductPrincipalManager() {
  const [tab, setTab] = useState(0);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const showSnackbar = (message, severity) =>
    setSnackbar({ open: true, message, severity });

  // ---------------- PRODUCTS ----------------
  const [products, setProducts] = useState([]);
  const [prodForm, setProdForm] = useState({ productName: "", description: "" });
  const [prodDialog, setProdDialog] = useState(false);
  const [editProdId, setEditProdId] = useState(null);
  const prodDeleteTimers = useRef({});
  const [pendingProdDeletes, setPendingProdDeletes] = useState({});
  const [prodPage, setProdPage] = useState(0);
  const [prodRowsPerPage, setProdRowsPerPage] = useState(5);
  const [totalProducts, setTotalProducts] = useState(0);
  const [file, setFile] = useState(null);

  const loadProducts = async (pageNum = prodPage, size = prodRowsPerPage) => {
    try {
      const res = await axiosInstances. productApi.get(`/products?page=${pageNum}&size=${size}`);
      console.log("Products API Response:", res.data);
      const result = res.data;
      const payload = result.data || {};
      setProducts(payload.data || result.content || result || []);
      setTotalProducts(payload.total || result.totalElements || (result.data?.length ?? 0));
    } catch (err) {
      console.error("Error fetching products", err);
      showSnackbar("Failed to load products", "error");
    }
  };

  const saveProduct = async () => {
    try {
      const token = localStorage.getItem("adminToken");


      // ✅ Handle optional file upload
      if (file) {
        const allowedTypes = [
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
          "application/vnd.ms-excel", // .xls
          "text/csv" // .csv
        ];

        if (!allowedTypes.includes(file.type)) {
          showSnackbar("Only Excel or CSV files are allowed for price uploads!", "error");
          return;
        }

        const formData = new FormData();
        formData.append("file", file);

        await axiosInstances. productApi.post(`/uploadProducts`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        showSnackbar("Price file uploaded successfully!", "success");
      } else {
        // ✅ Handle product save (add or update)
        if (editProdId) {
          await axiosInstances. productApi.post(`/product/update`, { id: editProdId, ...prodForm });
          showSnackbar("Product updated successfully", "success");
        } else {
          await axiosInstances. productApi.post(`/product`, prodForm);
          showSnackbar("Product added successfully", "success");
        }
      }

      // ✅ Final clean-up and refresh
      loadProducts();
      setProdDialog(false);
      setProdForm({ productName: "", description: "" });
      setEditProdId(null);
      setFile(null); // Clear file input if applicable

    } catch (err) {
      if (err.response && err.response.status === 409) {
        showSnackbar("Duplicate entry", "error");
      } else if (err.response && err.response.status === 401) {
        showSnackbar("Unauthorized - please log in", "error");
      } else {
        showSnackbar("Failed to save product or upload file", "error");
      }
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleProdDeleteClick = (id) => {
    setPendingProdDeletes((prev) => ({ ...prev, [id]: true }));
    prodDeleteTimers.current[id] = setTimeout(async () => {
      try {
        await axiosInstances. productApi.post(`/product/delete`, { id });
        showSnackbar("Product deleted", "success");
        loadProducts();
      } catch {
        showSnackbar("Delete failed", "error");
      } finally {
        setPendingProdDeletes((prev) => {
          const updated = { ...prev };
          delete updated[id];
          return updated;
        });
        prodDeleteTimers.current[id] = null;
      }
    }, 30000);
  };

  const handleProdUndoClick = (id) => {
    if (prodDeleteTimers.current[id]) {
      clearTimeout(prodDeleteTimers.current[id]);
      prodDeleteTimers.current[id] = null;
    }
    setPendingProdDeletes((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  // ---------------- PRINCIPALS ----------------
  const [principals, setPrincipals] = useState([]);
  const [princForm, setPrincForm] = useState({
    principalName: "",
    principalEmail: "",
    principalPhone: "",
    principalAddress: ""
  });
  const [princDialog, setPrincDialog] = useState(false);
  const [editPrincId, setEditPrincId] = useState(null);
  const princDeleteTimers = useRef({});
  const [pendingPrincDeletes, setPendingPrincDeletes] = useState({});
  const [princPage, setPrincPage] = useState(0);
  const [princRowsPerPage, setPrincRowsPerPage] = useState(5);
  const [totalPrincipals, setTotalPrincipals] = useState(0);

  const loadPrincipals = async (pageNum = princPage, size = princRowsPerPage) => {
    try {
      const res = await axiosInstances. productApi.get(`/principals?page=${pageNum}&size=${size}`);
      console.log("Principals API Response:", res.data);
      const result = res.data;
      const payload = result.data || {};
      setPrincipals(payload.data || result.content || result || []);
      setTotalPrincipals(payload.total || result.totalElements || (result.data?.length ?? 0));
    } catch (err) {
      console.error("Error fetching principals", err);
      showSnackbar("Failed to load principals", "error");
    }
  };

  const savePrincipal = async () => {
    try {
      if (editPrincId) {
        await axiosInstances. productApi.post(`/principal/update`, { id: editPrincId, ...princForm });
        showSnackbar("Principal updated successfully", "success");
      } else {
        await axiosInstances. productApi.post(`/principal`, princForm);
        showSnackbar("Principal added successfully", "success");
      }
      loadPrincipals();
      setPrincDialog(false);
      setPrincForm({ principalName: "", principalEmail: "", principalPhone: "", principalAddress: "" });
      setEditPrincId(null);
    } catch {
      showSnackbar("Failed to save principal", "error");
    }
  };

  const handlePrincDeleteClick = (id) => {
    setPendingPrincDeletes((prev) => ({ ...prev, [id]: true }));
    princDeleteTimers.current[id] = setTimeout(async () => {
      try {
        await axiosInstances. productApi.post(`/principal/delete`, { id });
        showSnackbar("Principal deleted", "success");
        loadPrincipals();
      } catch {
        showSnackbar("Delete failed", "error");
      } finally {
        setPendingPrincDeletes((prev) => {
          const updated = { ...prev };
          delete updated[id];
          return updated;
        });
        princDeleteTimers.current[id] = null;
      }
    }, 30000);
  };

  const handlePrincUndoClick = (id) => {
    if (princDeleteTimers.current[id]) {
      clearTimeout(princDeleteTimers.current[id]);
      princDeleteTimers.current[id] = null;
    }
    setPendingPrincDeletes((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  // ---------------- MAPPINGS ----------------
  const [maps, setMaps] = useState([]);
  const [mapForm, setMapForm] = useState({ productId: "", principalId: "", productCode: "" });
  const [mapDialog, setMapDialog] = useState(false);
  const [editMapId, setEditMapId] = useState(null);
  const mapDeleteTimers = useRef({});
  const [pendingMapDeletes, setPendingMapDeletes] = useState({});
  const [mapPage, setMapPage] = useState(0);
  const [mapRowsPerPage, setMapRowsPerPage] = useState(5);
  const [totalMaps, setTotalMaps] = useState(0);

  const loadMaps = async (pageNum = mapPage, size = mapRowsPerPage) => {
    try {
      const res = await axiosInstances. productApi.get(`/maps?page=${pageNum}&size=${size}`);
      console.log("Mappings API Response:", res.data);
      const result = res.data;
      const payload = result.data || {};
      setMaps(payload.data || result.content || result || []);
      setTotalMaps(payload.total || result.totalElements || (result.data?.length ?? 0));
    } catch (err) {
      console.error("Error fetching mappings", err);
      showSnackbar("Failed to load mappings", "error");
    }
  };

  const saveMap = async () => {
    try {
      if (editMapId) {
        await axiosInstances. productApi.post(`/map/update`, { id: editMapId, ...mapForm });
        showSnackbar("Mapping updated successfully", "success");
      } else {
        await axiosInstances. productApi.post(`/map`, mapForm);
        showSnackbar("Mapping added successfully", "success");
      }
      loadMaps();
      setMapDialog(false);
      setMapForm({ productId: "", principalId: "", productCode: "" });
      setEditMapId(null);
    } catch {
      showSnackbar("Failed to save mapping", "error");
    }
  };

  const handleMapDeleteClick = (id) => {
    setPendingMapDeletes((prev) => ({ ...prev, [id]: true }));
    mapDeleteTimers.current[id] = setTimeout(async () => {
      try {
        await axiosInstances. productApi.post(`/map/delete`, { id });
        showSnackbar("Mapping deleted", "success");
        loadMaps();
      } catch {
        showSnackbar("Delete failed", "error");
      } finally {
        setPendingMapDeletes((prev) => {
          const updated = { ...prev };
          delete updated[id];
          return updated;
        });
        mapDeleteTimers.current[id] = null;
      }
    }, 30000);
  };

  const handleMapUndoClick = (id) => {
    if (mapDeleteTimers.current[id]) {
      clearTimeout(mapDeleteTimers.current[id]);
      mapDeleteTimers.current[id] = null;
    }
    setPendingMapDeletes((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  // ---------------- EFFECTS ----------------
  useEffect(() => {
    loadProducts();
    loadPrincipals();
    loadMaps();
  }, []);

  // ---------------- UI ----------------
  return (
    <Container>
      <Tabs value={tab} onChange={(e, newVal) => setTab(newVal)} sx={{ mb: 2 }}>
        <Tab label="Products" />
        <Tab label="Principals" />
        <Tab label="Mappings" />
      </Tabs>

      {/* ✅ PRODUCTS TAB */}
      {tab === 0 && (
        <Box>
          <Button variant="contained" startIcon={<Add />} onClick={() => setProdDialog(true)}>
            Add Product
          </Button>
          <Table sx={{ mt: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Sr. No.</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((p, index) => (
                <TableRow key={p.id}>
                  <TableCell>{prodPage * prodRowsPerPage + index + 1}</TableCell>
                  <TableCell>{p.id}</TableCell>
                  <TableCell>{p.productName}</TableCell>
                  <TableCell>{p.description}</TableCell>
                  <TableCell>{p.status}</TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => {
                        setEditProdId(p.id);
                        setProdForm({ productName: p.productName, description: p.description });
                        setProdDialog(true);
                      }}
                    >
                      <Edit />
                    </IconButton>
                    {pendingProdDeletes[p.id] ? (
                      <IconButton color="warning" onClick={() => handleProdUndoClick(p.id)}>
                        <Undo />
                      </IconButton>
                    ) : (
                      <IconButton color="error" onClick={() => handleProdDeleteClick(p.id)}>
                        <Delete />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {products.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={totalProducts}
            page={prodPage}
            onPageChange={(e, newPage) => {
              setProdPage(newPage);
              loadProducts(newPage, prodRowsPerPage);
            }}
            rowsPerPage={prodRowsPerPage}
            onRowsPerPageChange={(e) => {
              const newSize = parseInt(e.target.value, 10);
              setProdRowsPerPage(newSize);
              setProdPage(0);
              loadProducts(0, newSize);
            }}
            rowsPerPageOptions={[5, 10, 25]}
            labelDisplayedRows={({ page }) =>
              `Page ${page + 1} of ${Math.ceil(Math.max(1, totalProducts) / prodRowsPerPage)}`
            }
          />

          <Dialog open={prodDialog} onClose={() => setProdDialog(false)}>
            <DialogTitle>{editProdId ? "Edit Product" : "Add Product"}</DialogTitle>
            <DialogContent>
              <TextField
                fullWidth
                label="Product Name"
                value={prodForm.productName}
                onChange={(e) => setProdForm({ ...prodForm, productName: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Description"
                value={prodForm.description}
                onChange={(e) => setProdForm({ ...prodForm, description: e.target.value })}
              />
              {!editProdId && (
                <>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    Upload File
                    <input
                      type="file"
                      hidden
                      onChange={handleFileChange}
                    />
                  </Button>
                  {file && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Selected: {file.name}
                    </Typography>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setProdDialog(false)}>Cancel</Button>
              <Button variant="contained" onClick={saveProduct}>Save</Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {/* ✅ PRINCIPALS TAB */}
      {tab === 1 && (
        <Box>
          <Button variant="contained" startIcon={<Add />} onClick={() => setPrincDialog(true)}>
            Add Principal
          </Button>
          <Table sx={{ mt: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Sr. No.</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {principals.map((p, index) => (
                <TableRow key={p.id}>
                  <TableCell>{princPage * princRowsPerPage + index + 1}</TableCell>
                  <TableCell>{p.id}</TableCell>
                  <TableCell>{p.principalName}</TableCell>
                  <TableCell>{p.principalEmail}</TableCell>
                  <TableCell>{p.principalPhone}</TableCell>
                  <TableCell>{p.principalAddress}</TableCell>
                  <TableCell>{p.status}</TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => {
                        setEditPrincId(p.id);
                        setPrincForm({
                          principalName: p.principalName,
                          principalEmail: p.principalEmail,
                          principalPhone: p.principalPhone,
                          principalAddress: p.principalAddress
                        });
                        setPrincDialog(true);
                      }}
                    >
                      <Edit />
                    </IconButton>
                    {pendingPrincDeletes[p.id] ? (
                      <IconButton color="warning" onClick={() => handlePrincUndoClick(p.id)}>
                        <Undo />
                      </IconButton>
                    ) : (
                      <IconButton color="error" onClick={() => handlePrincDeleteClick(p.id)}>
                        <Delete />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {principals.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={totalPrincipals}
            page={princPage}
            onPageChange={(e, newPage) => {
              setPrincPage(newPage);
              loadPrincipals(newPage, princRowsPerPage);
            }}
            rowsPerPage={princRowsPerPage}
            onRowsPerPageChange={(e) => {
              const newSize = parseInt(e.target.value, 10);
              setPrincRowsPerPage(newSize);
              setPrincPage(0);
              loadPrincipals(0, newSize);
            }}
            rowsPerPageOptions={[5, 10, 25]}
            labelDisplayedRows={({ page }) =>
              `Page ${page + 1} of ${Math.ceil(Math.max(1, totalPrincipals) / princRowsPerPage)}`
            }
          />

          <Dialog open={princDialog} onClose={() => setPrincDialog(false)}>
            <DialogTitle>{editPrincId ? "Edit Principal" : "Add Principal"}</DialogTitle>
            <DialogContent>
              <TextField
                fullWidth
                label="Name"
                value={princForm.principalName}
                onChange={(e) => setPrincForm({ ...princForm, principalName: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Email"
                value={princForm.principalEmail}
                onChange={(e) => setPrincForm({ ...princForm, principalEmail: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Phone"
                value={princForm.principalPhone}
                onChange={(e) => setPrincForm({ ...princForm, principalPhone: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Address"
                value={princForm.principalAddress}
                onChange={(e) => setPrincForm({ ...princForm, principalAddress: e.target.value })}
              />
              {!editPrincId && (
                <>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    Upload File
                    <input
                      type="file"
                      hidden
                      onChange={handleFileChange}
                    />
                  </Button>
                  {file && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Selected: {file.name}
                    </Typography>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPrincDialog(false)}>Cancel</Button>
              <Button variant="contained" onClick={savePrincipal}>Save</Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {/* ✅ MAPPINGS TAB */}
      {tab === 2 && (
        <Box>
          <Button variant="contained" startIcon={<Add />} onClick={() => setMapDialog(true)}>
            Add Mapping
          </Button>
          <Table sx={{ mt: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Sr. No.</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Principal</TableCell>
                <TableCell>Product Code</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {maps.map((m, index) => (
                <TableRow key={m.id}>
                  <TableCell>{mapPage * mapRowsPerPage + index + 1}</TableCell>
                  <TableCell>{m.id}</TableCell>
                  <TableCell>{m.product?.productName || m.productName}</TableCell>
                  <TableCell>{m.principal?.principalName || m.principalName}</TableCell>
                  <TableCell>{m.productCode}</TableCell>
                  <TableCell>{m.status}</TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => {
                        setEditMapId(m.id);
                        setMapForm({
                          productId: m.productId || m.product?.id || "",
                          principalId: m.principalId || m.principal?.id || "",
                          productCode: m.productCode
                        });
                        setMapDialog(true);
                      }}
                    >
                      <Edit />
                    </IconButton>
                    {pendingMapDeletes[m.id] ? (
                      <IconButton color="warning" onClick={() => handleMapUndoClick(m.id)}>
                        <Undo />
                      </IconButton>
                    ) : (
                      <IconButton color="error" onClick={() => handleMapDeleteClick(m.id)}>
                        <Delete />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {maps.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={totalMaps}
            page={mapPage}
            onPageChange={(e, newPage) => {
              setMapPage(newPage);
              loadMaps(newPage, mapRowsPerPage);
            }}
            rowsPerPage={mapRowsPerPage}
            onRowsPerPageChange={(e) => {
              const newSize = parseInt(e.target.value, 10);
              setMapRowsPerPage(newSize);
              setMapPage(0);
              loadMaps(0, newSize);
            }}
            rowsPerPageOptions={[5, 10, 25]}
            labelDisplayedRows={({ page }) =>
              `Page ${page + 1} of ${Math.ceil(Math.max(1, totalMaps) / mapRowsPerPage)}`
            }
          />

          {/* ✅ Add/Edit Mapping Dialog */}
          <Dialog open={mapDialog} onClose={() => setMapDialog(false)}>
            <DialogTitle>{editMapId ? "Edit Mapping" : "Add Mapping"}</DialogTitle>
            <DialogContent>
              <Select
                fullWidth
                value={mapForm.productId}
                onChange={(e) => setMapForm({ ...mapForm, productId: e.target.value })}
                displayEmpty
                sx={{ mb: 2 }}
              >
                <MenuItem value="">Select Product</MenuItem>
                {products.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.productName}</MenuItem>
                ))}
              </Select>

              <Select
                fullWidth
                value={mapForm.principalId}
                onChange={(e) => setMapForm({ ...mapForm, principalId: e.target.value })}
                displayEmpty
                sx={{ mb: 2 }}
              >
                <MenuItem value="">Select Principal</MenuItem>
                {principals.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.principalName}</MenuItem>
                ))}
              </Select>

              <TextField
                fullWidth
                label="Product Code"
                value={mapForm.productCode}
                onChange={(e) => setMapForm({ ...mapForm, productCode: e.target.value })}
              />              
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setMapDialog(false)}>Cancel</Button>
              <Button variant="contained" onClick={saveMap}>Save</Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {/* ✅ Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

